/*
  native-cart.js — UpCart replacement: cart drawer driven by the Section Rendering API.
  No libraries. Loaded with defer right BEFORE theme.js (see snippets/native-cart-head.liquid).
  Docs: docs/NATIVE-SWATCHES-CART.md

  Everything theme-specific is in ADAPTER below; the rest is theme-agnostic.
*/
(function () {
  'use strict';

  var cfg = window.nativeCart || {};
  var strings = cfg.strings || {};
  var SECTION = 'native-cart-drawer';

  /* ---------- Theme adapter (Impulse 9.x) ---------- */
  var ADAPTER = {
    // Links/buttons that open the cart
    openSelector: '.js-drawer-open-cart',
    // Header count bubble(s): toggled with this class when the cart has items
    bubbleSelector: '.cart-link__bubble',
    bubbleVisibleClass: 'cart-link__bubble--visible',
    // Events the theme fires after its own add-to-cart
    addedEvents: ['ajaxProduct:added'],
    // Don't hijack the cart link on the cart page itself
    isCartPage: function () {
      return document.body.classList.contains('template-cart');
    },
    // Replace Impulse's own drawer so it doesn't fetch/build the old markup
    disableThemeDrawer: function (controller) {
      hook('CartDrawer', function () {
        return function NativeCartDrawerProxy() {
          this.open = controller.open;
          this.close = controller.close;
        };
      });
    }
  };

  // Replace theme[name] with replacement(original) as soon as theme.js assigns it
  function hook(name, replacement) {
    window.theme = window.theme || {};
    var value;
    Object.defineProperty(theme, name, {
      configurable: true,
      enumerable: true,
      get: function () { return value; },
      set: function (original) { value = replacement(original); }
    });
  }

  /* ---------- Helpers ---------- */
  function rootUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || cfg.root || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function postJSON(path, body) {
    return fetch(rootUrl() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Native-Cart': '1' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok || json.status) {
          var err = new Error(json.description || json.message || 'Cart error');
          err.json = json;
          throw err;
        }
        return json;
      });
    });
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function storage(fn) {
    try { return fn(window.localStorage); } catch (e) { return null; }
  }

  /* ---------- Controller ---------- */
  var drawer, panel, lastFocus, busy = false, timerInterval, noteTimer, qtyTimer;

  var controller = {
    open: function () {
      if (!drawer) return;
      lastFocus = document.activeElement;
      drawer.hidden = false;
      document.documentElement.classList.add('native-cart-lock');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { drawer.classList.add('is-open'); });
      });
      panel.focus({ preventScroll: true });
      document.addEventListener('keydown', onKeydown);
      startTimer();
      loadFallbackUpsells();
    },

    close: function () {
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      var done = function () {
        drawer.hidden = true;
        document.documentElement.classList.remove('native-cart-lock');
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else window.setTimeout(done, 300);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    },

    // Re-render from the Section Rendering API
    refresh: function () {
      return fetch(rootUrl() + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (sections) { render(sections[SECTION]); });
    },

    add: function (variantId, quantity) {
      return postJSON('cart/add.js', {
        items: [{ id: Number(variantId), quantity: quantity || 1 }],
        sections: SECTION,
        sections_url: window.location.pathname
      }).then(function (json) {
        render(json.sections && json.sections[SECTION]);
        return json;
      });
    }
  };

  function render(html) {
    if (!html || !drawer) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-native-cart-inner]');
    var current = drawer.querySelector('[data-native-cart-inner]');
    if (!fresh || !current) return;

    var scroller = current.querySelector('[data-native-cart-scroll]');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var activeLine = document.activeElement && document.activeElement.closest('[data-native-cart-line]');
    var activeSel = activeLine ? describeFocus(document.activeElement, activeLine) : null;
    var fallbackUpsells = current.querySelector('[data-native-cart-upsells-fallback]');
    var oldBar = current.querySelector('[data-native-cart-shipping-bar]');
    var oldProgress = oldBar ? oldBar.style.getPropertyValue('--native-cart-progress') : '';

    current.replaceWith(fresh);

    // Start the fresh shipping bar at the old value so the change animates
    var freshBar = fresh.querySelector('[data-native-cart-shipping-bar]');
    if (freshBar && oldProgress) {
      freshBar.style.setProperty('--native-cart-progress', oldProgress);
      void freshBar.offsetWidth;
    }

    var newScroller = fresh.querySelector('[data-native-cart-scroll]');
    if (newScroller) newScroller.scrollTop = scrollTop;
    // keep already loaded recommendation upsells when nothing else changed them
    var upsellSlot = fresh.querySelector('[data-native-cart-upsells]');
    if (fallbackUpsells && upsellSlot && fresh.dataset.upsellCount === '0') upsellSlot.appendChild(fallbackUpsells);
    restoreFocus(fresh, activeSel);

    updateBubbles(Number(fresh.dataset.itemCount || 0));
    updateShipping(fresh);
    setupCarousel();
    startTimer();
    if (!drawer.hidden) loadFallbackUpsells();
    announce(strings.updated);
    document.dispatchEvent(new CustomEvent('native-cart:rendered', { detail: { itemCount: Number(fresh.dataset.itemCount || 0) } }));
  }

  // Keep keyboard focus on the same control after a re-render
  function describeFocus(el, line) {
    var attrs = ['data-native-cart-remove', 'data-native-cart-qty-input'];
    for (var i = 0; i < attrs.length; i++) if (el.hasAttribute(attrs[i])) return { key: line.dataset.key, sel: '[' + attrs[i] + ']' };
    if (el.hasAttribute('data-native-cart-qty-change')) {
      return { key: line.dataset.key, sel: '[data-native-cart-qty-change="' + el.getAttribute('data-native-cart-qty-change') + '"]' };
    }
    return null;
  }

  function restoreFocus(container, desc) {
    if (!desc) return;
    var line = container.querySelector('[data-native-cart-line][data-key="' + desc.key + '"]');
    var target = line && line.querySelector(desc.sel);
    if (target) target.focus({ preventScroll: true });
    else panel.focus({ preventScroll: true });
  }

  function announce(text) {
    var live = drawer && drawer.querySelector('[data-native-cart-live]');
    if (!live || !text) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = text; }, 60);
  }

  function showError(message) {
    announce(message || strings.error);
    var body = drawer.querySelector('[data-native-cart-scroll]') || drawer.querySelector('[data-native-cart-inner]');
    var box = drawer.querySelector('.native-cart__error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'native-cart__error';
      box.setAttribute('role', 'alert');
      body.insertBefore(box, body.firstChild);
    }
    box.textContent = message || strings.error;
  }

  function updateBubbles(count) {
    document.querySelectorAll(ADAPTER.bubbleSelector).forEach(function (b) {
      b.classList.toggle(ADAPTER.bubbleVisibleClass, count > 0);
    });
    document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) { el.textContent = count; });
  }

  /* ---------- Line changes ---------- */
  function changeLine(lineEl, quantity) {
    if (busy) return;
    busy = true;
    drawer.setAttribute('aria-busy', 'true');
    lineEl.classList.add('is-loading');
    postJSON('cart/change.js', {
      line: Number(lineEl.dataset.nativeCartLine),
      quantity: quantity,
      sections: SECTION,
      sections_url: window.location.pathname
    }).then(function (json) {
      render(json.sections && json.sections[SECTION]);
      if (quantity > 0) {
        // Shopify caps the quantity at stock silently: tell the customer
        var item = (json.items || []).find(function (i) { return i.key === lineEl.dataset.key; });
        if (item && item.quantity < quantity) showError(strings.maxQuantity.replace('[quantity]', item.quantity));
      }
    }).catch(function (err) {
      showError(err.message);
      controller.refresh();
    }).finally(function () {
      busy = false;
      drawer.removeAttribute('aria-busy');
    });
  }

  /* ---------- Discount codes (cart/update.js `discount`) ---------- */
  function appliedCodes() {
    return Array.prototype.map.call(drawer.querySelectorAll('[data-native-cart-code-remove]'), function (el) {
      return el.getAttribute('data-native-cart-code-remove');
    });
  }

  function setCodes(codes, newCode) {
    if (busy) return;
    busy = true;
    var error = drawer.querySelector('[data-native-cart-code-error]');
    postJSON('cart/update.js', { discount: codes.join(','), sections: SECTION, sections_url: window.location.pathname })
      .then(function (json) {
        var match = newCode && (json.discount_codes || []).find(function (c) { return c.code.toLowerCase() === newCode.toLowerCase(); });
        if (newCode && (!match || !match.applicable)) {
          // Not valid for this cart: remove it again, show the message
          busy = false;
          return postJSON('cart/update.js', { discount: codes.filter(function (c) { return c !== newCode; }).join(','), sections: SECTION, sections_url: window.location.pathname })
            .then(function (json2) {
              render(json2.sections && json2.sections[SECTION]);
              var err = drawer.querySelector('[data-native-cart-code-error]');
              if (err) err.hidden = false;
              announce(strings.codeInvalid);
            });
        }
        render(json.sections && json.sections[SECTION]);
      })
      .catch(function (err) {
        if (error) error.hidden = false;
        showError(err.message);
      })
      .finally(function () { busy = false; });
  }

  /* ---------- Free shipping bar (threshold in shop currency, converted for markets) ---------- */
  function updateShipping(scope) {
    var box = (scope || drawer).querySelector('[data-native-cart-shipping]');
    if (!box) return;
    var inner = drawer.querySelector('[data-native-cart-inner]');
    var rate = (window.Shopify && Shopify.currency && Number(Shopify.currency.rate)) || 1;
    var threshold = Math.round(Number(box.dataset.threshold || 0) * rate);
    var total = Number(inner.dataset.total || 0);
    var remaining = Math.max(threshold - total, 0);
    var pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
    var text = box.querySelector('[data-native-cart-shipping-text]');
    var bar = box.querySelector('[data-native-cart-shipping-bar]');
    text.innerHTML = remaining > 0
      ? strings.freeShippingRemaining.replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>')
      : strings.freeShippingReached;
    bar.setAttribute('aria-valuenow', pct);
    bar.style.setProperty('--native-cart-progress', pct + '%');
    box.classList.toggle('is-reached', remaining === 0);
  }

  /* ---------- Reservation timer (like UpCart) ----------
     Starts when the cart gets its first item and survives page loads and further adds
     (localStorage), like UpCart. It only resets when the cart becomes empty. At 00:00 UpCart
     hides the bar and keeps the cart: same here (onExpire 'hide'; 'restart' is optional). */
  var TIMER_KEY = 'nativeCartTimerEnd';

  function resetTimer() {
    storage(function (s) { s.removeItem(TIMER_KEY); });
  }

  function startTimer() {
    window.clearInterval(timerInterval);
    var bar = drawer && drawer.querySelector('[data-native-cart-timer]');
    var inner = drawer && drawer.querySelector('[data-native-cart-inner]');
    var count = inner ? Number(inner.dataset.itemCount || 0) : 0;
    if (!count) {
      resetTimer();
      return;
    }
    if (!bar || !cfg.timer || !cfg.timer.enabled) return;
    var timeEl = bar.querySelector('[data-native-cart-timer-time]');
    var end = Number(storage(function (s) { return s.getItem(TIMER_KEY); }) || 0);
    if (!end) {
      end = Date.now() + cfg.timer.minutes * 60000;
      storage(function (s) { s.setItem(TIMER_KEY, String(end)); });
    }
    var tick = function () {
      var left = Math.max(0, Math.round((end - Date.now()) / 1000));
      timeEl.textContent = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');
      if (left > 0) return;
      window.clearInterval(timerInterval);
      if (cfg.timer.onExpire === 'restart') {
        resetTimer();
        startTimer();
      } else {
        bar.hidden = true;
      }
    };
    tick();
    if (end > Date.now()) timerInterval = window.setInterval(tick, 1000);
  }

  /* ---------- Upsells: carousel layout (like UpCart on Smith & Granville) ---------- */
  var ARROW_PREV = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>';
  var ARROW_NEXT = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>';

  function setupCarousel() {
    var box = drawer && drawer.querySelector('[data-native-cart-upsells][data-layout="carousel"]');
    var list = box && box.querySelector('.native-cart__upsell-list');
    if (!list || list.closest('.native-cart-carousel')) return;

    var slides = Array.prototype.slice.call(list.children);
    var container = document.createElement('div');
    container.className = 'native-cart-carousel';
    container.setAttribute('role', 'region');
    container.setAttribute('aria-roledescription', 'carousel');
    container.setAttribute('aria-label', strings.upsellRegion || 'Products');
    container.innerHTML =
      '<button type="button" class="native-cart-carousel__btn" data-dir="-1" aria-label="' + (strings.upsellPrev || 'Previous') + '">' + ARROW_PREV + '</button>' +
      '<div class="native-cart-carousel__window"></div>' +
      '<button type="button" class="native-cart-carousel__btn" data-dir="1" aria-label="' + (strings.upsellNext || 'Next') + '">' + ARROW_NEXT + '</button>';
    list.parentNode.insertBefore(container, list);
    container.querySelector('.native-cart-carousel__window').appendChild(list);
    list.setAttribute('aria-live', 'polite');

    var index = 0;
    var prev = container.querySelector('[data-dir="-1"]');
    var next = container.querySelector('[data-dir="1"]');

    function update() {
      list.style.transform = 'translateX(' + (-100 * index) + '%)';
      prev.disabled = index === 0;
      next.disabled = index >= slides.length - 1;
      slides.forEach(function (slide, i) {
        var hidden = i !== index;
        slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', (i + 1) + ' / ' + slides.length);
        slide.querySelectorAll('a, button, select').forEach(function (el) {
          if (hidden) el.setAttribute('tabindex', '-1');
          else el.removeAttribute('tabindex');
        });
      });
    }

    container.addEventListener('click', function (evt) {
      var btn = evt.target.closest('.native-cart-carousel__btn');
      if (!btn || btn.disabled) return;
      index = Math.max(0, Math.min(slides.length - 1, index + Number(btn.dataset.dir)));
      update();
    });
    update();
  }

  /* ---------- Upsells: recommendations fallback ---------- */
  var upsellLoadedFor = null;

  function loadFallbackUpsells() {
    if (!cfg.upsells || !cfg.upsells.enabled || !cfg.upsells.fallback) return;
    var inner = drawer.querySelector('[data-native-cart-inner]');
    var slot = drawer.querySelector('[data-native-cart-upsells]');
    if (!inner || !slot || inner.dataset.upsellCount !== '0') return;
    var productId = inner.dataset.firstProductId;
    if (!productId) return;
    if (upsellLoadedFor === productId && slot.querySelector('[data-native-cart-upsells-fallback]')) return;
    upsellLoadedFor = productId;
    // the recommendations API returns max 10 products
    var url = cfg.recommendationsUrl + '?product_id=' + productId + '&limit=' + Math.min(10, cfg.upsells.limit + 4) + '&section_id=native-cart-upsells';
    fetch(url).then(function (res) { return res.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var block = doc.querySelector('[data-native-cart-upsells-fallback]');
      var target = drawer.querySelector('[data-native-cart-upsells]');
      if (!block || !target) return;
      target.innerHTML = '';
      target.appendChild(block);
      setupCarousel();
    }).catch(function () {});
  }

  /* ---------- Focus trap + Escape ---------- */
  function onKeydown(evt) {
    if (evt.key === 'Escape') {
      controller.close();
      return;
    }
    if (evt.key !== 'Tab') return;
    var focusables = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (evt.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && document.activeElement === last) {
      evt.preventDefault();
      first.focus();
    }
  }

  /* ---------- Event wiring ---------- */
  function onDrawerClick(evt) {
    var t = evt.target;
    if (t.closest('[data-native-cart-close]')) {
      evt.preventDefault();
      controller.close();
      return;
    }
    var line = t.closest('[data-native-cart-line]');
    if (line && t.closest('[data-native-cart-remove]')) {
      changeLine(line, 0);
      return;
    }
    var step = t.closest('[data-native-cart-qty-change]');
    if (line && step) {
      var input = line.querySelector('[data-native-cart-qty-input]');
      var qty = Math.max(0, Number(input.value || 0) + Number(step.getAttribute('data-native-cart-qty-change')));
      input.value = qty;
      changeLine(line, qty);
      return;
    }
    var add = t.closest('[data-native-cart-upsell-add]');
    if (add) {
      var item = add.closest('[data-native-cart-upsell]');
      var select = item && item.querySelector('[data-native-cart-upsell-variant]');
      add.classList.add('is-loading');
      add.disabled = true;
      controller.add(select ? select.value : add.getAttribute('data-native-cart-upsell-add'))
        .catch(function (err) { showError(err.message); add.disabled = false; add.classList.remove('is-loading'); });
      return;
    }
    if (t.closest('[data-native-cart-code-apply]')) {
      var field = drawer.querySelector('[data-native-cart-code-input]');
      var code = field.value.trim();
      if (!code) return;
      var codes = appliedCodes();
      if (codes.indexOf(code) === -1) codes.push(code);
      setCodes(codes, code);
      return;
    }
    var removeCode = t.closest('[data-native-cart-code-remove]');
    if (removeCode) {
      var drop = removeCode.getAttribute('data-native-cart-code-remove');
      setCodes(appliedCodes().filter(function (c) { return c !== drop; }));
    }
  }

  function onDrawerChange(evt) {
    var input = evt.target.closest('[data-native-cart-qty-input]');
    if (input) {
      var line = input.closest('[data-native-cart-line]');
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      window.clearTimeout(qtyTimer);
      qtyTimer = window.setTimeout(function () { changeLine(line, qty); }, 300);
    }
  }

  function onDrawerInput(evt) {
    var note = evt.target.closest('[data-native-cart-note]');
    if (note) {
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(function () {
        postJSON('cart/update.js', { note: note.value }).catch(function () {});
      }, 600);
    }
  }

  function onDrawerKeydown(evt) {
    if (evt.key === 'Enter' && evt.target.closest('[data-native-cart-code-input]')) {
      evt.preventDefault();
      drawer.querySelector('[data-native-cart-code-apply]').click();
    }
  }

  // Theme add-to-cart forms announce themselves via ADAPTER.addedEvents. Mark them so the
  // fetch/XHR watcher below doesn't open the drawer a second time.
  var themeAddPending = false;

  document.addEventListener('submit', function (evt) {
    if (evt.target && evt.target.matches && evt.target.matches('form[action*="/cart/add"]')) themeAddPending = true;
  }, true);

  function afterAdd() {
    controller.refresh().then(controller.open);
  }

  function themeAddedHandler(evt) {
    themeAddPending = false;
    // Impulse also fires the event (without detail) after a 422 error: don't open then
    if (!evt.detail || !evt.detail.product) return;
    afterAdd();
  }

  // Third-party add-to-cart (e.g. GemPages product pages) → refresh + open, like UpCart did
  function watchForeignAdds() {
    var isAdd = function (url) { return /\/cart\/add(\.js)?(\?|$)/.test(url); };
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var headers = (init && init.headers) || {};
      var mine = headers['X-Native-Cart'] || (typeof headers.get === 'function' && headers.get('X-Native-Cart'));
      var p = originalFetch.apply(this, arguments);
      if (isAdd(url) && !mine) {
        p.then(function (res) {
          if (res.ok && !themeAddPending) afterAdd();
        }).catch(function () {});
      }
      return p;
    };
    var send = XMLHttpRequest.prototype.send, openXhr = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._nativeCartUrl = url;
      return openXhr.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (isAdd(String(this._nativeCartUrl || ''))) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300 && !themeAddPending) afterAdd();
        });
      }
      return send.apply(this, arguments);
    };
  }

  function init() {
    drawer = document.querySelector('[data-native-cart]');
    if (!drawer) return;
    panel = drawer.querySelector('[data-native-cart-panel]');

    drawer.addEventListener('click', onDrawerClick);
    drawer.addEventListener('change', onDrawerChange);
    drawer.addEventListener('input', onDrawerInput);
    drawer.addEventListener('keydown', onDrawerKeydown);

    document.querySelectorAll(ADAPTER.openSelector).forEach(function (a) { a.setAttribute('aria-controls', drawer.id); });
    document.addEventListener('click', function (evt) {
      var trigger = evt.target.closest && evt.target.closest(ADAPTER.openSelector);
      if (!trigger || ADAPTER.isCartPage()) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      controller.open();
    }, true);

    ADAPTER.addedEvents.forEach(function (name) {
      document.addEventListener(name, themeAddedHandler);
    });
    document.addEventListener('cart:open', controller.open);
    document.addEventListener('cart:close', controller.close);
    document.addEventListener('cart:build', controller.refresh);

    updateShipping();
    startTimer();
    setupCarousel();
  }

  window.NativeCart = controller;
  ADAPTER.disableThemeDrawer(controller);
  if (cfg.watchForeignAdds !== false) watchForeignAdds();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
